import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as LucideIcons from "lucide-react";
import { fetchAbout, saveAbout } from "@/store/altingross/about";
import PageHeader from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Save, Plus, Trash2, Loader2, ImageIcon, Upload, Eye,
  Code2, GripVertical, Sparkles, Info, Search, X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// ─── Sabitler ──────────────────────────────────────────────────────────────
const IMAGE_BASE = "http://localhost:5297/";

const getImageUrl = (imageUrl) =>
  imageUrl ? `${IMAGE_BASE}${imageUrl}` : null;

const EMPTY_FEATURE = { icon: "", title: "", description: "" };

const INITIAL_VALUES = {
  title:          "",
  content:        "",
  experienceText: "",
  image:          null,
  features:       [{ ...EMPTY_FEATURE }],
};

// Seçilebilir Lucide ikon listesi
const ICON_LIST = [
  "Star","Award","Trophy","Medal","Crown","Badge",
  "Shield","ShieldCheck","Lock","Key","Fingerprint","ShieldAlert",
  "Zap","Rocket","Gauge","Timer","Clock","Hourglass",
  "Briefcase","Building","Building2","Store","BarChart","TrendingUp","PieChart",
  "Users","UserCheck","Heart","ThumbsUp","Handshake","Smile",
  "Cpu","Code","Code2","Globe","Wifi","Database","Server","Smartphone","Laptop",
  "Phone","Mail","MessageCircle","Bell","Send","MessageSquare",
  "Leaf","Sun","Sparkles","Gem","Diamond","Flower2",
  "Wrench","Settings","Settings2","Hammer","PenTool","Tool",
  "MapPin","Navigation","Compass","Map",
  "CheckCircle","CheckCircle2","CircleCheck","ListChecks",
  "DollarSign","CreditCard","Wallet","Coins","Banknote",
  "Package","Box","Gift","Target","Layers","Grid","List","LayoutGrid",
  "Image","Video","Music","Camera","Film",
  "BookOpen","FileText","Newspaper","BookMarked","GraduationCap",
  "Truck","Car","Plane","Ship","Bus",
  "Recycle","Droplets","Wind","Mountain","Trees",
  "Lightbulb","Telescope","Microscope","FlaskConical","Atom",
];

// ─── Lucide ikon render yardımcısı ─────────────────────────────────────────
function LucideIcon({ name, className }) {
  const Icon = LucideIcons[name];
  if (!Icon) return null;
  return <Icon className={className} />;
}

// ─── İkon Seçici ───────────────────────────────────────────────────────────
function IconPicker({ value, onChange }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState("");
  const containerRef        = useRef(null);

  const filtered = search.trim()
    ? ICON_LIST.filter((n) => n.toLowerCase().includes(search.toLowerCase()))
    : ICON_LIST;

  const SelectedIcon = value ? LucideIcons[value] : null;

  // Dışarı tıklayınca kapat
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (name) => {
    onChange(name);
    setOpen(false);
    setSearch("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <div ref={containerRef} className="relative">
      <Label className="text-[10px] text-slate-400">İkon</Label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "mt-0.5 h-8 w-full flex items-center gap-2 px-2 rounded-md border text-sm transition-colors",
          open
            ? "border-blue-500 bg-blue-50 text-blue-700"
            : "border-input bg-background text-slate-700 hover:border-blue-300"
        )}
      >
        {SelectedIcon ? (
          <>
            <SelectedIcon className="h-4 w-4 shrink-0 text-blue-600" />
            <span className="flex-1 text-left text-xs truncate">{value}</span>
            <span
              onClick={handleClear}
              className="text-slate-400 hover:text-red-500 transition-colors"
            >
              <X className="h-3 w-3" />
            </span>
          </>
        ) : (
          <>
            <LucideIcons.Smile className="h-4 w-4 text-slate-300 shrink-0" />
            <span className="flex-1 text-left text-slate-400 text-xs">İkon seç...</span>
          </>
        )}
      </button>

      {open && (
        <div className="absolute z-50 left-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2 w-72">
          {/* Arama */}
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              autoFocus
              placeholder="İkon ara... (örn: Star)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs"
            />
          </div>

          {/* İkon grid */}
          <div className="grid grid-cols-7 gap-0.5 max-h-52 overflow-y-auto">
            {filtered.map((name) => {
              const Icon = LucideIcons[name];
              if (!Icon) return null;
              return (
                <button
                  key={name}
                  type="button"
                  title={name}
                  onClick={() => handleSelect(name)}
                  className={cn(
                    "h-9 w-9 flex items-center justify-center rounded-md transition-colors",
                    value === name
                      ? "bg-blue-100 text-blue-600"
                      : "text-slate-600 hover:bg-slate-100"
                  )}
                >
                  <Icon className="h-4 w-4" />
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="col-span-7 text-center text-slate-400 text-xs py-4">Sonuç bulunamadı</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────
export default function AboutPage() {
  const dispatch   = useDispatch();
  const { data: aboutData, loading, saveLoading } = useSelector((s) => s.about);

  const [values, setValues]             = useState(INITIAL_VALUES);
  const [imagePreview, setImagePreview] = useState(null);
  const [activeTab, setActiveTab]       = useState("edit");
  const fileInputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchAbout());
  }, [dispatch]);

  useEffect(() => {
    if (aboutData) {
      setValues({
        title:          aboutData.title          ?? "",
        content:        aboutData.content        ?? "",
        experienceText: aboutData.experienceText ?? "",
        image:          null,
        features:       aboutData.features?.length
          ? aboutData.features.map((f) => ({ icon: f.icon ?? "", title: f.title ?? "", description: f.description ?? "" }))
          : [{ ...EMPTY_FEATURE }],
      });
      if (aboutData.imageUrl) setImagePreview(getImageUrl(aboutData.imageUrl));
    }
  }, [aboutData]);

  const set = (field, value) => setValues((v) => ({ ...v, [field]: value }));

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    set("image", file);
    setImagePreview(URL.createObjectURL(file));
  };

  const addFeature    = () => setValues((v) => ({ ...v, features: [...v.features, { ...EMPTY_FEATURE }] }));
  const removeFeature = (i) => setValues((v) => ({ ...v, features: v.features.filter((_, idx) => idx !== i) }));
  const updateFeature = useCallback((i, field, val) =>
    setValues((v) => ({
      ...v,
      features: v.features.map((f, idx) => idx === i ? { ...f, [field]: val } : f),
    })), []);

  const handleSave = async () => {
    if (!values.title.trim()) { toast.error("Başlık alanı zorunludur"); return; }
    const isUpdate = !!aboutData?.id;
    try {
      await dispatch(saveAbout({ data: { ...values, id: aboutData?.id }, isUpdate })).unwrap();
      toast.success(isUpdate ? "Hakkımızda sayfası güncellendi" : "Hakkımızda sayfası oluşturuldu");
      dispatch(fetchAbout());
    } catch {
      toast.error("Kaydetme başarısız");
    }
  };

  if (loading) {
    return (
      <div data-testid="about-page">
        <PageHeader testId="about-header" title="Hakkımızda Sayfası" description="Web sitesi hakkımızda içeriğini yönetin." />
        <div className="flex items-center justify-center py-32 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Yükleniyor...
        </div>
      </div>
    );
  }

  return (
    <div data-testid="about-page" className="flex flex-col h-full">
      <PageHeader
        testId="about-header"
        title="Hakkımızda Sayfası"
        description="Web sitesi hakkımızda içeriğini yönetin. Form sol tarafta, canlı önizleme sağ tarafta."
        action={
          <div className="flex items-center gap-2.5">
            {aboutData?.id ? (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 font-medium">Düzenleme Modu (PUT)</Badge>
            ) : (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 font-medium">İlk Kayıt (POST)</Badge>
            )}
            <Button onClick={handleSave} disabled={saveLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-sm shadow-blue-200 min-w-[110px]">
              {saveLoading
                ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Kaydediliyor...</>
                : <><Save className="h-4 w-4 mr-1.5" />Kaydet</>}
            </Button>
          </div>
        }
      />

      <div className="flex gap-5 flex-1 min-h-0">
        {/* ── SOL: Form ──────────────────────────────────────────────────── */}
        <div className="flex-1 min-w-0 overflow-y-auto space-y-5 pr-1" style={{ maxHeight: "calc(100vh - 180px)" }}>

          <div className="glass-card p-5 space-y-4">
            <SectionTitle>Temel Bilgiler</SectionTitle>
            <div>
              <Label htmlFor="title" className="text-xs text-slate-500">Başlık *</Label>
              <Input id="title" value={values.title} onChange={(e) => set("title", e.target.value)} placeholder="Hakkımızda başlığı" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="experienceText" className="text-xs text-slate-500">Deneyim Metni</Label>
              <Input id="experienceText" value={values.experienceText} onChange={(e) => set("experienceText", e.target.value)} placeholder="Örn: 20+ Yıllık Deneyim" className="mt-1" />
            </div>
          </div>

          <div className="glass-card p-5 space-y-3">
            <SectionTitle>Görsel</SectionTitle>
            <div onClick={() => fileInputRef.current?.click()}
              className={cn("relative h-40 rounded-lg border-2 border-dashed cursor-pointer overflow-hidden transition-colors",
                imagePreview ? "border-transparent" : "border-slate-200 hover:border-blue-400 bg-slate-50")}>
              {imagePreview ? (
                <>
                  <img src={imagePreview} alt="Önizleme" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-sm font-medium flex items-center gap-2"><Upload className="h-4 w-4" />Değiştir</span>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2">
                  <ImageIcon className="h-8 w-8" />
                  <span className="text-sm">Resim seçmek için tıklayın</span>
                  {aboutData?.id && <span className="text-xs text-slate-300">Seçmezseniz mevcut resim korunur</span>}
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
          </div>

          <div className="glass-card p-5 space-y-3">
            <div className="flex items-center justify-between">
              <SectionTitle>İçerik (HTML)</SectionTitle>
              <div className="flex gap-1 bg-slate-100 rounded-md p-0.5">
                <TabBtn active={activeTab === "edit"}    onClick={() => setActiveTab("edit")}    icon={Code2}>Kod</TabBtn>
                <TabBtn active={activeTab === "preview"} onClick={() => setActiveTab("preview")} icon={Eye}>Önizle</TabBtn>
              </div>
            </div>
            {activeTab === "edit" ? (
              <Textarea value={values.content} onChange={(e) => set("content", e.target.value)}
                placeholder="<p>İçerik HTML kodu...</p>" rows={10}
                className="font-mono text-xs resize-none bg-slate-950 text-green-400 border-slate-700 focus:border-blue-500 rounded-md" />
            ) : (
              <div className="rounded-md border border-slate-200 overflow-hidden h-[260px]">
                <iframe srcDoc={values.content || "<p style='color:#94a3b8;padding:16px'>İçerik girilmedi</p>"}
                  title="İçerik Önizleme" sandbox="allow-same-origin" className="w-full h-full bg-white" />
              </div>
            )}
          </div>

          <div className="glass-card p-5 space-y-4">
            <div className="flex items-center justify-between">
              <SectionTitle>Özellikler</SectionTitle>
              <Button type="button" size="sm" variant="outline" onClick={addFeature}
                className="h-8 rounded-md text-blue-600 border-blue-200 hover:bg-blue-50">
                <Plus className="h-3.5 w-3.5 mr-1" />Özellik Ekle
              </Button>
            </div>
            {values.features.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">Henüz özellik yok.</p>
            )}
            <div className="space-y-3">
              {values.features.map((f, i) => (
                <FeatureRow key={i} index={i} feature={f} onChange={updateFeature}
                  onRemove={() => removeFeature(i)} removable={values.features.length > 1} />
              ))}
            </div>
          </div>
        </div>

        {/* ── SAĞ: Canlı Önizleme ──────────────────────────────────────── */}
        <div className="w-[42%] shrink-0 overflow-y-auto" style={{ maxHeight: "calc(100vh - 180px)" }}>
          <div className="glass-card overflow-hidden sticky top-0">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
              <Eye className="h-4 w-4 text-slate-400" />
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Canlı Önizleme</span>
            </div>

            <div className="p-6 space-y-5">
              {values.title ? (
                <h1 className="text-2xl font-bold text-slate-900 leading-tight">{values.title}</h1>
              ) : (
                <div className="h-8 bg-slate-100 rounded-md animate-pulse" />
              )}

              {imagePreview && (
                <div className="rounded-lg overflow-hidden h-44">
                  <img src={imagePreview} alt="Önizleme" className="w-full h-full object-cover" />
                </div>
              )}

              {values.content && (
                <div className="rounded-md border border-slate-100 overflow-hidden">
                  <iframe srcDoc={values.content} title="İçerik" sandbox="allow-same-origin"
                    className="w-full bg-white" style={{ height: "220px" }} />
                </div>
              )}

              {values.experienceText && (
                <div className="flex items-center gap-2 bg-blue-50 rounded-lg px-4 py-3">
                  <Sparkles className="h-4 w-4 text-blue-500 shrink-0" />
                  <span className="text-sm font-semibold text-blue-700">{values.experienceText}</span>
                </div>
              )}

              {values.features.some((f) => f.title) && (
                <>
                  <Separator className="bg-slate-100" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-3">Özellikler</p>
                    <div className="grid grid-cols-1 gap-3">
                      {values.features.filter((f) => f.title).map((f, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                          {f.icon && (
                            <div className="h-9 w-9 rounded-md bg-blue-100 flex items-center justify-center shrink-0">
                              <LucideIcon name={f.icon} className="h-5 w-5 text-blue-600" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-slate-800">{f.title}</p>
                            {f.description && (
                              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{f.description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {!values.title && !values.content && !values.experienceText && (
                <div className="text-center py-10 text-slate-300">
                  <Info className="h-10 w-10 mx-auto mb-2" />
                  <p className="text-sm">Form alanlarını doldurun, önizleme burada görünecek.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Alt bileşenler ────────────────────────────────────────────────────────
function SectionTitle({ children }) {
  return <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest">{children}</p>;
}

function TabBtn({ active, onClick, icon: Icon, children }) {
  return (
    <button type="button" onClick={onClick}
      className={cn("flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors",
        active ? "bg-white shadow-sm text-blue-600" : "text-slate-500 hover:text-slate-700")}>
      <Icon className="h-3 w-3" />{children}
    </button>
  );
}

function FeatureRow({ index, feature, onChange, onRemove, removable }) {
  return (
    <div className="flex gap-2 items-start bg-slate-50 rounded-lg p-3 border border-slate-100">
      <GripVertical className="h-4 w-4 text-slate-300 mt-7 shrink-0" />

      <div className="flex-1 grid grid-cols-1 gap-2">
        <div className="grid grid-cols-3 gap-2 items-end">
          <IconPicker value={feature.icon} onChange={(val) => onChange(index, "icon", val)} />
          <div className="col-span-2">
            <Label className="text-[10px] text-slate-400">Başlık</Label>
            <Input value={feature.title} onChange={(e) => onChange(index, "title", e.target.value)}
              placeholder="Özellik başlığı" className="mt-0.5 h-8 text-sm" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] text-slate-400">Açıklama</Label>
          <Input value={feature.description} onChange={(e) => onChange(index, "description", e.target.value)}
            placeholder="Kısa açıklama..." className="mt-0.5 h-8 text-sm" />
        </div>
      </div>

      {removable && (
        <button type="button" onClick={onRemove}
          className="h-8 w-8 flex items-center justify-center rounded-md text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors mt-7 shrink-0">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
